<script setup lang="ts">
// 账号 / 密钥 新建·编辑模态。只收发明文 draft；封装/指纹在 vault-session 完成。
// 编辑时密码/密钥值字段留空 = 保留原值（不回显明文，内存卫生）。
import { reactive, computed, ref } from 'vue'
import { generatePassword, defaultPolicy, estimateScore } from '../lib/generator'
import type { AccountItem, SecretItem, Category, Project } from '../lib/model'
import type { AccountDraft, SecretDraft } from '../vault-session'

const props = defineProps<{
  kind: 'account' | 'secret'
  entry: AccountItem | SecretItem | null
  categories: Category[]
  projects: Project[]
}>()
const emit = defineEmits<{
  (e: 'save-account', d: AccountDraft): void
  (e: 'save-secret', d: SecretDraft): void
  (e: 'close'): void
}>()

const isEdit = computed(() => !!props.entry)
const a = props.kind === 'account' ? (props.entry as AccountItem | null) : null
const s = props.kind === 'secret' ? (props.entry as SecretItem | null) : null

const acc = reactive<AccountDraft>({
  id: a?.id,
  platform_name: a?.platform_name ?? '',
  platform_url: a?.platform_url ?? '',
  username: a?.username ?? '',
  email: a?.email ?? '',
  phone: a?.phone ?? '',
  password: '',
  category_id: a?.category_id ?? '',
  project_id: a?.project_id ?? '',
  has_2fa: a?.has_2fa ?? false,
  favorite: a?.favorite ?? false,
  note: '',
})
const sec = reactive<SecretDraft>({
  id: s?.id,
  name: s?.name ?? '',
  platform_name: s?.platform_name ?? '',
  kind: s?.kind ?? 'API Key',
  key_id: s?.key_id ?? '',
  secret: '',
  environment: s?.environment ?? 'personal',
  project_id: s?.project_id ?? '',
  rotation_cycle_days: s?.rotation_cycle_days ?? null,
  note: '',
})

const pwScore = computed(() => (acc.password ? estimateScore(acc.password) : -1))
const scoreLabel = ['很弱', '弱', '中等', '强', '很强']

function gen() {
  acc.password = generatePassword(defaultPolicy())
}

const err = ref('')
function submit() {
  err.value = ''
  if (props.kind === 'account') {
    if (!acc.platform_name.trim()) return (err.value = '平台名称必填')
    emit('save-account', { ...acc })
  } else {
    if (!sec.name.trim()) return (err.value = '名称必填')
    if (!isEdit.value && !sec.secret?.trim()) return (err.value = '密钥值必填')
    emit('save-secret', { ...sec })
  }
}
</script>

<template>
  <div class="kt-modal" role="dialog" aria-modal="true" @keydown.esc="emit('close')">
    <div class="kt-modal__panel">
      <header class="kt-modal__head">
        <h2>{{ isEdit ? '编辑' : '新建' }}{{ kind === 'account' ? '账号' : '密钥' }}</h2>
        <button class="kt-btn kt-btn--ghost kt-btn--sm" @click="emit('close')" aria-label="关闭">✕</button>
      </header>

      <!-- 账号 -->
      <div v-if="kind === 'account'" class="kt-form">
        <label class="kt-label">平台名称 *</label>
        <input class="kt-input" v-model="acc.platform_name" placeholder="如 GitHub" />
        <label class="kt-label">网址</label>
        <input class="kt-input" v-model="acc.platform_url" placeholder="https://" />
        <div class="kt-row">
          <div><label class="kt-label">用户名</label><input class="kt-input" v-model="acc.username" /></div>
          <div><label class="kt-label">邮箱</label><input class="kt-input" v-model="acc.email" /></div>
        </div>
        <label class="kt-label">密码{{ isEdit ? '（留空保留原值）' : '' }}</label>
        <div class="kt-pwrow">
          <input class="kt-input" v-model="acc.password" :placeholder="isEdit ? '••••（不改则留空）' : '输入或一键生成'" autocomplete="off" />
          <button type="button" class="kt-btn kt-btn--gold kt-btn--sm kt-pwgen" @click="gen">生成</button>
        </div>
        <div v-if="pwScore >= 0" class="kt-strength" :data-score="pwScore">
          <span class="kt-strength__bar"><i :style="{ width: ((pwScore + 1) / 5 * 100) + '%' }" /></span>
          <span class="kt-strength__label">{{ scoreLabel[pwScore] }}</span>
        </div>
        <div class="kt-row">
          <div>
            <label class="kt-label">分类</label>
            <select class="kt-input" v-model="acc.category_id">
              <option value="">（无）</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div>
            <label class="kt-label">项目</label>
            <select class="kt-input" v-model="acc.project_id">
              <option value="">（无）</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
        </div>
        <label class="kt-check"><input type="checkbox" v-model="acc.has_2fa" /> 已开启二次验证（2FA）</label>
        <label class="kt-check"><input type="checkbox" v-model="acc.favorite" /> 收藏</label>
        <label class="kt-label">备注{{ isEdit ? '（留空保留原值）' : '' }}</label>
        <textarea class="kt-input kt-textarea" v-model="acc.note" rows="2" />
      </div>

      <!-- 密钥 -->
      <div v-else class="kt-form">
        <label class="kt-label">名称 *</label>
        <input class="kt-input" v-model="sec.name" placeholder="如 OpenAI 生产 Key" />
        <div class="kt-row">
          <div>
            <label class="kt-label">类型</label>
            <select class="kt-input" v-model="sec.kind">
              <option>API Key</option><option>Access Key</option><option>Token</option><option>SSH Key</option><option>Secret</option>
            </select>
          </div>
          <div>
            <label class="kt-label">环境</label>
            <select class="kt-input" v-model="sec.environment">
              <option value="personal">个人</option><option value="dev">开发</option><option value="test">测试</option><option value="prod">生产</option>
            </select>
          </div>
        </div>
        <label class="kt-label">所属平台</label>
        <input class="kt-input" v-model="sec.platform_name" placeholder="如 OpenAI / AWS" />
        <label class="kt-label">Key ID</label>
        <input class="kt-input" v-model="sec.key_id" placeholder="如 sk-proj-xxxx" />
        <label class="kt-label">密钥值 {{ isEdit ? '（留空保留原值）' : '*' }}</label>
        <input class="kt-input kt-mono" v-model="sec.secret" :placeholder="isEdit ? '••••（不改则留空）' : ''" autocomplete="off" />
        <div class="kt-row">
          <div>
            <label class="kt-label">项目</label>
            <select class="kt-input" v-model="sec.project_id">
              <option value="">（无）</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div>
            <label class="kt-label">轮换周期（天）</label>
            <input class="kt-input" type="number" v-model.number="sec.rotation_cycle_days" min="0" />
          </div>
        </div>
        <label class="kt-label">备注{{ isEdit ? '（留空保留原值）' : '' }}</label>
        <textarea class="kt-input kt-textarea" v-model="sec.note" rows="2" />
      </div>

      <p v-if="err" class="kt-error" role="alert">{{ err }}</p>
      <div class="kt-modal__foot">
        <button class="kt-btn kt-btn--ghost" @click="emit('close')">取消</button>
        <button class="kt-btn kt-btn--primary" @click="submit">{{ isEdit ? '保存' : '创建' }}</button>
      </div>
    </div>
  </div>
</template>
